// SAFE-WRITE-BEGIN: frontend/src/pages/Handoff.tsx
/**
 * PLAT-ADMIN-HANDOFF-1 — landing page for a super_admin coming from the Valuation site.
 * The one-time code arrives in the URL FRAGMENT; it is read once, removed from the address bar
 * at once, and kept only in a ref (never state, never rendered). Opening this page signs nobody
 * in: only the person's click redeems the code, and the destination is always /hub.
 */
import { useEffect, useRef, useState } from 'react'
import { previewHandoff, redeemHandoff, HANDOFF_INVALID_MESSAGE } from '@/api/platformHandoff'
import { Alert, AlertDescription } from '@/components/ui/alert'

type Phase = 'loading' | 'ready' | 'invalid'

function takeCodeFromFragment(): string | null {
  const raw = window.location.hash.replace(/^#/, '')
  const code = new URLSearchParams(raw).get('c')
  window.history.replaceState(null, '', (window.location.pathname || '/handoff') + (window.location.search || ''))
  return code && code.length > 0 ? code : null
}

export function Handoff() {
  const codeRef = useRef<string | null | undefined>(undefined)
  const [phase, setPhase] = useState<Phase>('loading')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (codeRef.current === undefined) codeRef.current = takeCodeFromFragment()
    const code = codeRef.current
    if (!code) { setPhase('invalid'); return }
    let live = true
    previewHandoff(code)
      .then(e => { if (live) { setEmail(e); setPhase('ready') } })
      .catch(() => { if (live) setPhase('invalid') })
    return () => { live = false }
  }, [])

  async function onContinue() {
    const code = codeRef.current
    if (!code || busy) return
    setBusy(true)
    try {
      await redeemHandoff(code)
      codeRef.current = null
      window.location.assign('/hub')       // fixed destination; nothing from the server or the URL is used
    } catch {
      codeRef.current = null
      setPhase('invalid')
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-6 text-center">
        {phase === 'loading' && <p className="text-sm text-muted-foreground">Checking your sign-in link…</p>}
        {phase === 'ready' && (
          <>
            <p className="text-sm">{`Continue as ${email}`}</p>
            <button type="button" onClick={onContinue} disabled={busy}
              className="w-full rounded bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60">
              Continue
            </button>
          </>
        )}
        {phase === 'invalid' && (
          <>
            <Alert variant="destructive"><AlertDescription>{HANDOFF_INVALID_MESSAGE}</AlertDescription></Alert>
            <a href="/login" className="text-sm underline">Sign in</a>
          </>
        )}
      </div>
    </div>
  )
}
// SAFE-WRITE-EOF: frontend/src/pages/Handoff.tsx
